import {
  RISK_MEASURE_URL,
  type RiskGapConfidenceRating,
  RiskGapModel,
  riskGapConfidenceRatings,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { searchAllRecords } from '@ctw/shared/api/fqs-rest/search-helpers';
import { searchFirstPartyRecords } from '@ctw/shared/api/fqs-rest/search-helpers';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { QUERY_KEY_PATIENT_RISK_GAPS } from '@ctw/shared/utils/query-keys';

/*
Gets patient risk gaps with UI exclusions applied.
*/
export const usePatientRiskGapsWithUIExclusions = () => {
  const builderConfig = useBuilderConfig();

  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_RISK_GAPS,
    queryFn: async ({ requestContext, ctwFetch, patient }) => {
      if (!patient.UPID) {
        return [];
      }

      const riskGaps = (
        await searchFirstPartyRecords(
          ctwFetch,
          {
            env: requestContext.env,
            builderId: requestContext.builderId,
            authToken: requestContext.authToken,
          },
          'MeasureReport',
          {
            upid: patient.UPID ?? '',
            _count: '200',
          },
        )
      )
        .filter((report) => {
          return report.measure === RISK_MEASURE_URL;
        })
        .map((report) => {
          return new RiskGapModel(report);
        });

      let filteredRiskGaps = riskGaps;

      // Filter out risk gaps with excluded HCC codes
      const excludedHccCodes = builderConfig.riskGaps?.uiExcludedHccCodes ?? [];
      if (excludedHccCodes.length > 0) {
        filteredRiskGaps = filteredRiskGaps.filter((riskGap) => {
          return !excludedHccCodes.includes(riskGap.hccCode);
        });
      }

      // Filter out risk gaps based on confidence level
      const confidenceLevelThreshold = builderConfig.riskGaps?.displayGapsWithConfidenceLevelsAbove;
      if (
        confidenceLevelThreshold &&
        riskGapConfidenceRatings.includes(confidenceLevelThreshold as RiskGapConfidenceRating)
      ) {
        const thresholdIndex = riskGapConfidenceRatings.indexOf(
          confidenceLevelThreshold as RiskGapConfidenceRating,
        );

        filteredRiskGaps = filteredRiskGaps.filter((riskGap) => {
          if (!riskGap.confidenceRating) {
            return true;
          }
          const riskGapIndex = riskGapConfidenceRatings.indexOf(riskGap.confidenceRating);
          return riskGapIndex > thresholdIndex;
        });
      }
      return filteredRiskGaps;
    },
  });
};

interface GetPatientRiskGapArgs {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  ids: Array<string>;
}

export const getRiskGapsByIds = async ({
  ctwFetch,
  requestContext,
  ids,
}: GetPatientRiskGapArgs) => {
  return (
    await searchAllRecords(ctwFetch, requestContext, 'MeasureReport', {
      _id: ids.join(','),
    })
  ).map((riskGap) => new RiskGapModel(riskGap));
};
